import type { Note } from '../../types/note';
import { getEventTypeIcon, getEventTypeLabel, getEventTypeBgColor, EventType } from '../../types/eventTypes';
import { calculateGioYearNumber, formatGioYearText } from '../../types/gioTypes';
import RecurringBadge from './RecurringBadge';

interface NoteItemProps {
    note: Note;
    onEdit: (note: Note) => void;
    onDelete: (id: string) => void;
}

const NoteItem: React.FC<NoteItemProps> = ({ note, onEdit, onDelete }) => {
    const icon = note.eventType ? getEventTypeIcon(note.eventType) : '📝';
    const label = note.eventType ? getEventTypeLabel(note.eventType) : 'Khác';
    const bgColor = note.eventType ? getEventTypeBgColor(note.eventType) : 'bg-gray-50';

    // Calculate năm thứ X for Giỗ events (Feature A3)
    const gioYearNumber = note.eventType === EventType.GIO && note.eventData?.deathYear
        ? calculateGioYearNumber(note.eventData.deathYear as number | string)
        : null;

    return (
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-3">
            <div className="flex justify-between items-start">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Event Type Icon */}
                    <div className={`w - 10 h - 10 rounded - lg ${bgColor} flex items - center justify - center flex - shrink - 0`}>
                        <span className="text-xl">{icon}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                        {/* Event Type Label + Recurring Badge */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-white/60">{label}</span>
                            <RecurringBadge
                                recurring={note.recurring}
                                yearNumber={note.yearNumber}
                                compact
                            />
                        </div>

                        <h4 className="text-white font-medium truncate">
                            {note.title || 'Không có tiêu đề'}
                        </h4>

                        {/* Năm thứ X for Giỗ (Feature A3) */}
                        {gioYearNumber && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-400 mt-1">
                                🕯️ {formatGioYearText(gioYearNumber)}
                            </span>
                        )}

                        {note.content && (
                            <p className="text-white/70 text-sm mt-1 line-clamp-2">
                                {note.content}
                            </p>
                        )}

                        {note.tags && note.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                                {note.tags.map((tag, index) => (
                                    <span
                                        key={index}
                                        className="px-2 py-0.5 bg-white/20 rounded-full text-xs text-white"
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex gap-2 ml-3">
                    <button
                        onClick={() => onEdit(note)}
                        className="p-2 hover:bg-white/20 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                        title="Sửa"
                    >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => onDelete(note.id || note.localId || '')}
                        className="p-2 hover:bg-red-500/50 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                        title="Xóa"
                    >
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Sync status & Pinned indicator */}
            <div className="flex items-center justify-between mt-2">
                {note.synced === false && (
                    <div className="flex items-center text-xs text-yellow-400">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                        </svg>
                        Chưa đồng bộ
                    </div>
                )}
                {note.pinned && (
                    <span className="text-yellow-400 text-sm">📌 Ghim</span>
                )}
                {/* Show full recurring badge on its own line if enabled */}
                {note.recurring?.enabled && !note.synced && (
                    <RecurringBadge
                        recurring={note.recurring}
                        yearNumber={note.yearNumber}
                    />
                )}
            </div>
        </div>
    );
};

export default NoteItem;
